import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export { API, BACKEND_URL };

// ============== API HELPERS ==============
export const api = {
  getSports: () => axios.get(`${API}/sports`),
  getLeagues: (sportId) => axios.get(`${API}/leagues${sportId ? `?sport_id=${sportId}` : ''}`),
  getTeams: (leagueId) => axios.get(`${API}/teams${leagueId ? `?league_id=${leagueId}` : ''}`),
  getGames: (params = {}) => {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value) query.append(key, value);
    });
    return axios.get(`${API}/games${query.toString() ? `?${query.toString()}` : ''}`);
  },
  getGame: (id) => axios.get(`${API}/games/${id}`),
  // Public cross-collection search (teams + leagues + games)
  search: (q, limit = 8) => axios.get(`${API}/search`, { params: { q, limit } }),
  // Paginated + searchable admin-only listings
  listAdminGames: ({ page = 1, per_page = 50, search = '', league_id = '', status = '', is_featured } = {}) => {
    const params = { page, per_page };
    if (search) params.search = search;
    if (league_id) params.league_id = league_id;
    if (status) params.status = status;
    if (is_featured !== undefined && is_featured !== null && is_featured !== '') params.is_featured = is_featured;
    return axios.get(`${API}/admin/games/list`, { params });
  },
  listAdminTeams: ({ page = 1, per_page = 50, search = '', league_id = '' } = {}) => {
    const params = { page, per_page };
    if (search) params.search = search;
    if (league_id) params.league_id = league_id;
    return axios.get(`${API}/admin/teams/list`, { params });
  },
  getLiveGames: () => axios.get(`${API}/games/live`),
  getLiveNowGames: () => axios.get(`${API}/games/live-now`),
  getRecentReplays: (limit = 12) => axios.get(`${API}/games/recent-replays?limit=${limit}`),
  getVideoPlayerAd: () => axios.get(`${API}/ads/serve/video_player`),
  getFeaturedGames: () => axios.get(`${API}/games/featured`),
  getUpcomingGames: (limit = 20) => axios.get(`${API}/games/upcoming?limit=${limit}`),
  getReplayGames: (limit = 20) => axios.get(`${API}/games/replays?limit=${limit}`),
  getGameLiveScore: (id) => axios.get(`${API}/games/${id}/live-score`),
  getFixtures: (params = {}) => {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value) query.append(key, value);
    });
    return axios.get(`${API}/fixtures${query.toString() ? `?${query.toString()}` : ''}`);
  },
  getLiveScores: () => axios.get(`${API}/live-scores`),
  getStats: () => axios.get(`${API}/stats`),
  
  // Admin APIs
  createSport: (data) => axios.post(`${API}/admin/sports`, data),
  updateSport: (id, data) => axios.put(`${API}/admin/sports/${id}`, data),
  deleteSport: (id) => axios.delete(`${API}/admin/sports/${id}`),
  createLeague: (data) => axios.post(`${API}/admin/leagues`, data),
  updateLeague: (id, data) => axios.put(`${API}/admin/leagues/${id}`, data),
  deleteLeague: (id) => axios.delete(`${API}/admin/leagues/${id}`),
  createTeam: (data) => axios.post(`${API}/admin/teams`, data),
  updateTeam: (id, data) => axios.put(`${API}/admin/teams/${id}`, data),
  deleteTeam: (id) => axios.delete(`${API}/admin/teams/${id}`),
  
  // Partner APIs
  getPartners: (isActive = null) => axios.get(`${API}/partners${isActive !== null ? `?is_active=${isActive}` : ''}`),
  createPartner: (data) => axios.post(`${API}/admin/partners`, data),
  updatePartner: (id, data) => axios.put(`${API}/admin/partners/${id}`, data),
  deletePartner: (id) => axios.delete(`${API}/admin/partners/${id}`),
  
  // Page Banners APIs
  getPageBanners: () => axios.get(`${API}/page-banners`),
  getPageBanner: (slug) => axios.get(`${API}/page-banners/${slug}`),
  updatePageBanner: (slug, data) => axios.put(`${API}/admin/page-banners/${slug}`, data),
  initPageBanners: () => axios.post(`${API}/admin/page-banners/init`),
  
  // Game Management
  createGame: (data) => axios.post(`${API}/admin/games`, data),
  updateGame: (id, data) => axios.put(`${API}/admin/games/${id}`, data),
  deleteGame: (id) => axios.delete(`${API}/admin/games/${id}`),
  
  // Fixture Management
  createFixture: (data) => axios.post(`${API}/admin/fixtures`, data),
  updateFixture: (id, data) => axios.put(`${API}/admin/fixtures/${id}`, data),
  deleteFixture: (id) => axios.delete(`${API}/admin/fixtures/${id}`),
  
  // Live Score Management (OLD - deprecated)
  createLiveScore: (data) => axios.post(`${API}/admin/live-scores`, data),
  updateLiveScore: (id, data) => axios.put(`${API}/admin/live-scores/${id}`, data),
  deleteLiveScore: (id) => axios.delete(`${API}/admin/live-scores/${id}`),
  
  // Score Control Panel APIs (NEW - unified with games)
  getGamesForScoreControl: (showAll = false) => axios.get(`${API}/admin/games/live-control${showAll ? '?show_all=true' : ''}`),
  updateGameScore: (id, data) => axios.put(`${API}/admin/games/${id}/score`, data),
  
  // Database Seed
  seedDatabase: () => axios.post(`${API}/admin/seed`),
  
  // Media Library APIs
  getMedia: (tag, opts = {}) => {
    const params = new URLSearchParams();
    if (tag) params.set('tag', tag);
    if (opts.search) params.set('search', opts.search);
    params.set('skip', opts.skip ?? 0);
    params.set('limit', opts.limit ?? 2000);
    return axios.get(`${API}/admin/media?${params.toString()}`);
  },
  uploadMedia: (formData) => axios.post(`${API}/admin/media/upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  updateMedia: (id, data) => axios.put(`${API}/admin/media/${id}`, null, { params: data }),
  replaceMedia: (id, formData) => axios.post(`${API}/admin/media/${id}/replace`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deleteMedia: (id) => axios.delete(`${API}/admin/media/${id}`),
  
  // Site Settings APIs
  getSettings: () => axios.get(`${API}/settings`),
  updateSettings: (data) => axios.put(`${API}/admin/settings`, data),
  
  // Club Management APIs
  getClubs: () => axios.get(`${API}/clubs`),
  getClub: (id) => axios.get(`${API}/clubs/${id}`),
  createClub: (data) => axios.post(`${API}/clubs`, data),
  updateClub: (id, data) => axios.put(`${API}/clubs/${id}`, data),
  deleteClub: (id) => axios.delete(`${API}/clubs/${id}`),
  getClubMatches: (clubId) => axios.get(`${API}/clubs/${clubId}/matches`),
  createClubMatch: (clubId, data) => axios.post(`${API}/clubs/${clubId}/matches`, data),
  updateClubMatch: (clubId, matchId, data) => axios.put(`${API}/clubs/${clubId}/matches/${matchId}`, data),
  deleteClubMatch: (clubId, matchId) => axios.delete(`${API}/clubs/${clubId}/matches/${matchId}`),
  generateMatchGraphic: (clubId, matchId, eventType) => 
    axios.post(`${API}/clubs/${clubId}/matches/${matchId}/graphics`, { event_type: eventType }),
  getMatchGraphics: (clubId, matchId) => axios.get(`${API}/clubs/${clubId}/matches/${matchId}/graphics`),
  generatePromoGraphic: (gameId, format) => axios.post(`${API}/admin/games/${gameId}/promo-graphic`, { format }),
  
  // Team Names autocomplete
  searchTeamNames: (query) => axios.get(`${API}/team-names/search?q=${encodeURIComponent(query)}`),
  addTeamName: (name) => axios.post(`${API}/team-names`, { name }),
  
  // Auth APIs
  login: (data) => axios.post(`${API}/auth/login`, data),
  register: (data) => axios.post(`${API}/auth/register`, data),
  verifyEmail: (token) => axios.post(`${API}/auth/verify-email?token=${token}`),
  getMe: () => axios.get(`${API}/auth/me`),
  requestPasswordReset: (email) => axios.post(`${API}/auth/forgot-password`, { email }),
  resetPassword: (token, password) => axios.post(`${API}/auth/reset-password`, { token, new_password: password }),
  
  // Match Center APIs
  getMatch: (clubId, matchId) => axios.get(`${API}/clubs/${clubId}/matches/${matchId}`),
  createMatch: (clubId, data) => axios.post(`${API}/clubs/${clubId}/matches`, data),
  getPlayers: (clubId) => axios.get(`${API}/clubs/${clubId}/players`),
  createPlayer: (clubId, data) => axios.post(`${API}/clubs/${clubId}/players`, data),
  deletePlayer: (clubId, playerId) => axios.delete(`${API}/clubs/${clubId}/players/${playerId}`),
  updateMatchStatus: (clubId, matchId, status) => axios.put(`${API}/clubs/${clubId}/matches/${matchId}/status`, { status }),
  addGoal: (clubId, matchId, data) => axios.post(`${API}/clubs/${clubId}/matches/${matchId}/goals`, data),
  removeGoal: (clubId, matchId, goalId) => axios.delete(`${API}/clubs/${clubId}/matches/${matchId}/goals/${goalId}`),
  editScore: (clubId, matchId, data) => axios.put(`${API}/clubs/${clubId}/matches/${matchId}/score`, data),
  addMatchEvent: (clubId, matchId, data) => axios.post(`${API}/clubs/${clubId}/matches/${matchId}/events`, data),
  generateGraphic: (clubId, matchId, eventType) => axios.post(`${API}/clubs/${clubId}/matches/${matchId}/graphics`, { event_type: eventType }),
  
  // Upcoming Streams
  getUpcomingStreams: () => axios.get(`${API}/admin/upcoming-streams`),
  
  // Nearby Matches (within 72 hours)
  getNearbyMatches: () => axios.get(`${API}/matches/nearby`),
  
  // Production Companies (WPSL TV stream provider model)
  getProductionCompanies: () => axios.get(`${API}/production-companies`),
  getProductionCompany: (id) => axios.get(`${API}/production-companies/${id}`),
  getProductionCompaniesAdmin: () => axios.get(`${API}/admin/production-companies`),
  createProductionCompany: (data) => axios.post(`${API}/admin/production-companies`, data),
  updateProductionCompany: (id, data) => axios.put(`${API}/admin/production-companies/${id}`, data),
  deleteProductionCompany: (id) => axios.delete(`${API}/admin/production-companies/${id}`),

  // Bulk CSV import of teams under conferences
  bulkImportTeams: (teams) => axios.post(`${API}/admin/teams/bulk-import`, { teams }),
  bulkImportTeamsRich: (teams, auto_create_conferences = true) =>
    axios.post(`${API}/admin/teams/bulk-import`, { teams, auto_create_conferences }),
  bulkDeleteTeams: (team_ids) => axios.post(`${API}/admin/teams/bulk-delete`, { team_ids }),
  bulkAssignTeamsConference: (team_ids, league_id) => axios.post(`${API}/admin/teams/bulk-assign-conference`, { team_ids, league_id }),
  bulkMoveTeamsConference: (team_ids, league_id) => axios.post(`${API}/admin/teams/bulk-move-conference`, { team_ids, league_id }),
  exportTeamsCsv: () => axios.get(`${API}/admin/teams/export.csv`, { responseType: 'blob' }),
  bulkImportGames: (games, updateExisting = false) => axios.post(`${API}/admin/games/bulk-import`, { games, update_existing: updateExisting }),
  unfeatureAllGames: () => axios.post(`${API}/admin/games/unfeature-all`),
  auditGames: () => axios.get(`${API}/admin/games/audit`),
  applyAuditFixes: (fix_ids) => axios.post(`${API}/admin/games/audit/apply`, { fix_ids }),
  sendAuditEmailNow: () => axios.post(`${API}/admin/games/audit/send-now`),
  previewAuditEmail: () => axios.get(`${API}/admin/games/audit/preview-email`),
  bulkDeleteGames: (game_ids) => axios.post(`${API}/admin/games/bulk-delete`, { game_ids }),
  bulkSetFeaturedGames: (game_ids, is_featured) => axios.post(`${API}/admin/games/bulk-set-featured`, { game_ids, is_featured }),
  bulkSetProductionCompanyGames: (game_ids, production_company_id) => axios.post(`${API}/admin/games/bulk-set-production-company`, { game_ids, production_company_id }),
  bulkDeleteMedia: (media_ids) => axios.post(`${API}/admin/media/bulk-delete`, { media_ids }),
  bulkAddTagMedia: (media_ids, tag) => axios.post(`${API}/admin/media/bulk-add-tag`, { media_ids, tag }),

  // Admin user management (RBAC)
  listAdminUsers: () => axios.get(`${API}/admin/users`),
  inviteAdmin: (data) => axios.post(`${API}/admin/users/invite`, data),
  resendInvite: (user_id) => axios.post(`${API}/admin/users/${user_id}/resend-invite`),
  manualInviteLink: (user_id) => axios.get(`${API}/admin/users/${user_id}/manual-invite-link`),
  updateAdminRole: (user_id, role) => axios.patch(`${API}/admin/users/${user_id}/role`, { role }),
  deleteAdminUser: (user_id) => axios.delete(`${API}/admin/users/${user_id}`),
  setupPassword: (token, password, name) => axios.post(`${API}/auth/setup-password`, { token, password, name }),
  exportGamesCsv: () => axios.get(`${API}/admin/games/export.csv`, { responseType: 'blob' }),
  gamesCsvTemplate: () => axios.get(`${API}/admin/games/template.csv`, { responseType: 'blob' }),
  
  // Newsletter
  subscribeNewsletter: (email) => axios.post(`${API}/newsletter/subscribe`, { email }),
  
  // Locations
  getLocations: () => axios.get(`${API}/locations`),
  createLocation: (data) => axios.post(`${API}/admin/locations`, data),
  deleteLocation: (id) => axios.delete(`${API}/admin/locations/${id}`),
  
  // Ad Platform Admin APIs
  getAdvertisers: () => axios.get(`${API}/admin/advertisers`),
  getAdPricing: () => axios.get(`${API}/admin/ad-pricing`),
  updateAdPricing: (data) => axios.put(`${API}/admin/ad-pricing`, data),
  getManualSponsors: () => axios.get(`${API}/admin/manual-sponsors`),
  createManualSponsor: (data) => axios.post(`${API}/admin/manual-sponsors`, data),
  updateManualSponsor: (id, data) => axios.put(`${API}/admin/manual-sponsors/${id}`, data),
  deleteManualSponsor: (id) => axios.delete(`${API}/admin/manual-sponsors/${id}`),
};

export default api;
